import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { ApiResponse } from "../types/api";
import { notify } from "../context/FeedbackContext";

const DEV_FALLBACK_BACKEND_URL = "http://localhost:8000/api/v1";

const normalizeBackendUrl = (url: string): string => {
  const trimmedUrl = url.replace(/\/+$/, "");

  if (trimmedUrl.endsWith("/api/v1")) {
    return trimmedUrl;
  }

  if (trimmedUrl.endsWith("/api")) {
    return `${trimmedUrl}/v1`;
  }

  return `${trimmedUrl}/api/v1`;
};

/**
 * Resolve the backend base URL.
 *
 * - In development we allow an implicit fallback to `http://localhost:8000/api/v1`
 *   so `npm run dev` works without a `.env`.
 * - In production `VITE_API_URL` MUST be set; otherwise we throw at startup rather
 *   than silently hitting a hardcoded host (the previous behavior pointed at
 *   `https://cms.prepstripe.com`, which leaked an environment secret into the bundle).
 */
const resolveBackendUrl = (): string => {
  // `import.meta.env` carries a `[key: string]: any` index signature from
  // `vite/client`, so a direct read resolves to `any` under eslint's type-aware
  // linting. Route through `unknown` + a typeof guard to keep the assignment
  // type-safe without resorting to `any`.
  const rawEnvUrl: unknown = import.meta.env.VITE_API_URL;
  const envUrl: string | undefined =
    typeof rawEnvUrl === "string" ? rawEnvUrl : undefined;

  if (envUrl !== undefined && envUrl.trim().length > 0) {
    return normalizeBackendUrl(envUrl.trim());
  }

  if (import.meta.env.DEV) {
    return DEV_FALLBACK_BACKEND_URL;
  }

  throw new Error(
    "VITE_API_URL is required in production builds. Set it in your build environment before running `vite build`."
  );
};

// Create an Axios instance with a base URL for v1 API routes
const apiBaseUrl = resolveBackendUrl();
const authBaseUrl = apiBaseUrl.replace(/\/v1$/, "");

const api: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Create a separate Axios instance for auth endpoints (at /api level, not /api/v1)
export const authApi: AxiosInstance = axios.create({
  baseURL: authBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Add auth token if available
const addAuthToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = localStorage.getItem("token");
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

api.interceptors.request.use(addAuthToken, (error: unknown) => {
  return Promise.reject(error instanceof Error ? error : new Error(String(error)));
});

// Add auth token to authApi requests as well
authApi.interceptors.request.use(addAuthToken, (error: unknown) => {
  return Promise.reject(error instanceof Error ? error : new Error(String(error)));
});

const isAxiosErrorWithStatus = (error: unknown): error is AxiosError<unknown, unknown> =>
  axios.isAxiosError(error);

interface SessionExpiredContext {
  status: number;
  config: { url?: string };
}

const extractSessionExpiredContext = (
  error: unknown
): SessionExpiredContext | null => {
  if (!isAxiosErrorWithStatus(error)) {
    return null;
  }
  const status = error.response?.status ?? 0;
  if (status !== 401) {
    return null;
  }
  const configUrl = error.config?.url;
  return { status, config: { url: configUrl } };
};

// Response interceptor - Handle common errors (401, 403, etc.)
const handleResponseError = (error: unknown): Promise<unknown> => {
  const ctx = extractSessionExpiredContext(error);

  if (ctx !== null) {
    const requestUrl = String(ctx.config.url ?? "");
    const isLoginRequest = requestUrl.endsWith("/login");

    if (isLoginRequest) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }

    localStorage.removeItem("token");

    if (window.location.pathname !== "/login") {
      sessionStorage.setItem("session_expired", "1");
      sessionStorage.setItem(
        "redirect_after_login",
        `${window.location.pathname}${window.location.search}`
      );
      notify.error("Your session expired. Please sign in again.", "Session expired");
      window.location.assign("/login");
    }
  }

  return Promise.reject(error instanceof Error ? error : new Error(String(error)));
};

/**
 * Back-compat pagination bridge.
 *
 * The backend returns list pagination under `meta.pagination` (see the
 * `HandlesApiResponses` trait). Newer code reads that directly, but the legacy
 * `context/Admin/*` providers read a top-level `response.data.pagination`.
 * To keep both working during the react-query migration, mirror
 * `meta.pagination` onto a top-level `pagination` field when it is absent.
 * This never overwrites an existing value and leaves `data` untouched.
 */
const bridgePagination = (
  response: AxiosResponse<ApiResponse<unknown>>
): AxiosResponse<ApiResponse<unknown>> => {
  const body = response.data as
    | (ApiResponse<unknown> & { pagination?: unknown })
    | undefined;
  const metaPagination = body?.meta?.pagination;
  if (body && metaPagination !== undefined && body.pagination === undefined) {
    body.pagination = metaPagination;
  }
  return response;
};

api.interceptors.response.use(bridgePagination, handleResponseError);

// Add response interceptor to authApi as well for consistency
authApi.interceptors.response.use(bridgePagination, handleResponseError);

export default api;

